/**
 * インタラクション透過＆スナップ配置モジュール (InteractionPassthrough)
 * aidlc-docs/construction/desktop_ambient_platform/docs/interaction_passthrough_component.md 準拠
 */

import type { DisplayWorkArea, IPlatformBridgeAdapter } from './bridges/IPlatformBridgeAdapter';
import type { TransparentWindowManager } from './TransparentWindowManager';

export type SnapCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export class InteractionPassthrough {
  private static readonly DEFAULT_MARGIN = 24;
  private readonly bridge: IPlatformBridgeAdapter;
  private readonly windowManager: TransparentWindowManager;
  private isClickThroughState: boolean = false;
  private escapeKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(bridge: IPlatformBridgeAdapter, windowManager: TransparentWindowManager) {
    this.bridge = bridge;
    this.windowManager = windowManager;
  }

  /**
   * クリック透過（作業邪魔防止モード）の切り替え
   */
  public async setClickThrough(enable: boolean): Promise<void> {
    this.isClickThroughState = enable;
    await this.bridge.setClickThrough(enable);

    // デッドロック防止: ブラウザ/DOM環境でEscapeキーによる復帰リスナーをバインド
    if (typeof window !== 'undefined' && window.addEventListener) {
      if (enable && !this.escapeKeyHandler) {
        this.escapeKeyHandler = (e: KeyboardEvent) => {
          if (e.key === 'Escape' || (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 't')) {
            this.setClickThrough(false).catch((err) =>
              console.error('[InteractionPassthrough] Failed to revert clickThrough:', err)
            );
          }
        };
        window.addEventListener('keydown', this.escapeKeyHandler);
      } else if (!enable && this.escapeKeyHandler) {
        window.removeEventListener('keydown', this.escapeKeyHandler);
        this.escapeKeyHandler = null;
      }
    }
  }

  /**
   * 現在のクリック透過状態
   */
  public isClickThrough(): boolean {
    return this.isClickThroughState;
  }

  /**
   * 画面四隅へのスナップ幾何座標の計算
   */
  public calculateSnapCoordinates(
    corner: SnapCorner,
    windowSize: { width: number; height: number },
    workArea: DisplayWorkArea,
    margin: number = InteractionPassthrough.DEFAULT_MARGIN
  ): { x: number; y: number } {
    const safeMargin = Math.max(0, margin);

    switch (corner) {
      case 'top-left':
        return {
          x: workArea.x + safeMargin,
          y: workArea.y + safeMargin,
        };
      case 'top-right':
        return {
          x: workArea.x + workArea.width - windowSize.width - safeMargin,
          y: workArea.y + safeMargin,
        };
      case 'bottom-left':
        return {
          x: workArea.x + safeMargin,
          y: workArea.y + workArea.height - windowSize.height - safeMargin,
        };
      case 'bottom-right':
      default:
        return {
          x: workArea.x + workArea.width - windowSize.width - safeMargin,
          y: workArea.y + workArea.height - windowSize.height - safeMargin,
        };
    }
  }

  /**
   * 指定した四隅へのスナップ配置を実行
   */
  public async snapToCorner(
    corner: SnapCorner,
    margin: number = InteractionPassthrough.DEFAULT_MARGIN
  ): Promise<void> {
    const currentBounds = await this.windowManager.getBounds();
    const workArea = await this.bridge.getWorkArea();

    const targetCoords = this.calculateSnapCoordinates(
      corner,
      { width: currentBounds.width, height: currentBounds.height },
      workArea,
      margin
    );

    await this.windowManager.setBounds({
      x: targetCoords.x,
      y: targetCoords.y,
    });
  }

  /**
   * リソース解放
   */
  public destroy(): void {
    if (this.escapeKeyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.escapeKeyHandler);
      this.escapeKeyHandler = null;
    }
    this.isClickThroughState = false;
  }
}
